import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ActionIconButton from '../../Shared/ActionIconButton';
import { useSearchParams } from "react-router-dom";
import { useUI } from "../../Context/UIContext";
import ConfirmationModal from "../../Shared/confirmationModal";
import TableLoadingOverlay from "../../Shared/TableLoadingOverlay";
import AdminTableScrollLayout from "../../Shared/AdminTableScrollLayout";
import AdminPageHeader from "../../Shared/AdminPageHeader";
import { adminDataTableHeadSx } from "../../../utils/adminTableScrollSx";
import { usePermissions } from "../../Context/permissionsProvider";
import useEditorViewportHeight from "../newsletter-editor/hooks/useEditorViewportHeight";
import {
  deleteArticle,
  deleteArticleCategory,
  listArticleCategories,
  listArticles,
  saveArticleCategory,
  toggleArticleVisibility,
} from "../../../services/contentServices";
import ArticleEditorForm from "./article-editor/ArticleEditorForm";
import { clearArticleEditorSession } from "./article-editor/articleEditorSession";
import {
  contentFieldSx,
  contentFillTableContainerSx,
  contentGestioneGridSx,
  contentPageShellSx,
  contentPanelFillInsetSx,
  contentSectionMenubarSx,
  contentSectionMenubarTitleSx,
  contentSectionMenubarToolbarSx,
  contentSectionPaperSx,
  contentTabsSx,
  contentViewportGridFillSx,
  contentViewportPanelSx,
  contentViewportSectionPaperSx,
} from "./contentEditorStyles";

const TAB_GESTIONE = "gestione";
const TAB_EDITOR = "editor";

const normalizeTabKey = (key) => {
  if (key === TAB_EDITOR || key === "nuovo") return TAB_EDITOR;
  if (key === "categorie" || key === "articoli") return TAB_GESTIONE;
  return TAB_GESTIONE;
};

const tabIndexFromKey = (key) => (normalizeTabKey(key) === TAB_EDITOR ? 1 : 0);

const tabKeyFromIndex = (index) => (index === 1 ? TAB_EDITOR : TAB_GESTIONE);

const formatDate = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("it-IT", { dateStyle: "medium" }).format(new Date(value));
};

const GestioneMagazineContenuti = () => {
  const { isActionVisible } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const gestionePanelRef = useRef(null);
  const gestionePanelHeight = useEditorViewportHeight(gestionePanelRef);
  const { showError, showSuccess } = useUI();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = normalizeTabKey(searchParams.get("tab"));
  const tabIndex = tabIndexFromKey(activeTab);
  const editingArticleId = searchParams.get("articleId") || undefined;

  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [categoryInput, setCategoryInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState(null);
  const [deleteArticleTarget, setDeleteArticleTarget] = useState(null);
  const [visibilityUpdatingId, setVisibilityUpdatingId] = useState(null);
  const [editorSessionKey, setEditorSessionKey] = useState(0);

  const orderedCategories = useMemo(
    () => [...categories].sort((a, b) => a.name.localeCompare(b.name)),
    [categories]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    const errors = [];
    try {
      try {
        setCategories(await listArticleCategories());
      } catch (error) {
        errors.push(error.message);
        setCategories([]);
      }
      try {
        setArticles(await listArticles({ category: categoryFilter || undefined }));
      } catch (error) {
        errors.push(error.message);
        setArticles([]);
      }
      if (errors.length) {
        showError(errors.join(" "));
      }
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, showError]);

  useEffect(() => {
    if (tabIndex === 0) {
      fetchData();
    }
  }, [fetchData, tabIndex]);

  const handleTabChange = (_, newIndex) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tabKeyFromIndex(newIndex));
    setSearchParams(next, { replace: true });
  };

  const openNewArticleTab = () => {
    clearArticleEditorSession();
    const next = new URLSearchParams(searchParams);
    next.set("tab", TAB_EDITOR);
    next.delete("articleId");
    setSearchParams(next, { replace: true });
    setEditorSessionKey((value) => value + 1);
  };

  const openEditArticleTab = (articleId) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", TAB_EDITOR);
    next.set("articleId", articleId);
    setSearchParams(next, { replace: true });
  };

  const handleCreateCategory = async (event) => {
    event.preventDefault();
    const name = categoryInput.trim();
    if (!name) return;
    try {
      await saveArticleCategory(name);
      setCategoryInput("");
      showSuccess("Categoria salvata.");
      await fetchData();
    } catch (error) {
      showError(error.message);
    }
  };

  const handleDeleteCategory = async () => {
    try {
      await deleteArticleCategory(deleteCategoryTarget.name);
      setDeleteCategoryTarget(null);
      showSuccess("Categoria eliminata.");
      await fetchData();
    } catch (error) {
      showError(error.message);
    }
  };

  const handleDeleteArticle = async () => {
    try {
      await deleteArticle(deleteArticleTarget.id);
      setDeleteArticleTarget(null);
      showSuccess("Articolo eliminato.");
      await fetchData();
    } catch (error) {
      showError(error.message);
    }
  };

  const handleToggleArticleVisibility = async (article) => {
    const nextVisible = !article.isVisible;
    setVisibilityUpdatingId(article.id);
    try {
      await toggleArticleVisibility(article.id, nextVisible);
      showSuccess(nextVisible ? "Articolo reso visibile." : "Articolo nascosto.");
      await fetchData();
    } catch (error) {
      showError(error.message);
    } finally {
      setVisibilityUpdatingId(null);
    }
  };

  const handleArticleSaved = (saved) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", TAB_EDITOR);
    next.set("articleId", String(saved.id));
    setSearchParams(next, { replace: true });
    fetchData();
  };

  const renderGestione = () => (
    <Box ref={gestionePanelRef} sx={contentViewportPanelSx(gestionePanelHeight)}>
      <Box sx={{ ...contentGestioneGridSx, ...contentViewportGridFillSx }}>
        <Paper elevation={0} sx={contentViewportSectionPaperSx}>
          <AppBar position="static" color="default" elevation={0} sx={contentSectionMenubarSx}>
            <Toolbar sx={{...contentSectionMenubarToolbarSx, justifyContent: "space-between"}}>
              <Typography component="h2" sx={contentSectionMenubarTitleSx}>
                Categorie
              </Typography>
              <Stack
                component="form"
                direction="row"
                spacing={1}
                alignItems="right"
                onSubmit={handleCreateCategory}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Nuova categoria"
                  value={categoryInput}
                  onChange={(event) => setCategoryInput(event.target.value)}
                  sx={{...contentFieldSx, backgroundColor: "#fff"}}
                />
                <Button
                  type="submit"
                  size="small"
                  variant="outlined"
                  sx={{
                    whiteSpace: "nowrap",
                    width: 32,
                    minWidth: 32,
                    minHeight: 32,
                    p: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AddIcon />
                </Button>
              </Stack>
            </Toolbar>
          </AppBar>
          <Box sx={contentPanelFillInsetSx}>
            <AdminTableScrollLayout
            containerSx={contentFillTableContainerSx(loading)}
            tableMinWidth={280}
            head={
              <TableHead sx={adminDataTableHeadSx}>
                <TableRow>
                  <TableCell sx={{ py: 1 }}>Nome</TableCell>
                  <TableCell align="right" sx={{ py: 1, width: 56 }}>
                    Azioni
                  </TableCell>
                </TableRow>
              </TableHead>
            }
            body={
              <TableBody>
                {!loading && orderedCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nessuna categoria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  orderedCategories.map((category, index) => (
                    <TableRow key={category.name} sx={{ backgroundColor: index % 2 === 0 ? "white" : "#f8fafc" }}>
                      <TableCell sx={{ py: 0.75 }}>{category.name}</TableCell>
                      <TableCell align="right" sx={{ py: 0.25 }}>
                        {isActionVisible("gestioneMagazine", "eliminaCategoria") && (
                          <ActionIconButton
                            title="Elimina categoria"
                            onClick={() => setDeleteCategoryTarget(category)}
                            icon={DeleteIcon}
                            color="#f44336"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            }
          />
          </Box>
        </Paper>

        <Paper elevation={0} sx={contentViewportSectionPaperSx}>
          <AppBar position="static" color="default" elevation={0} sx={contentSectionMenubarSx}>
            <Toolbar sx={contentSectionMenubarToolbarSx}>
              <Typography component="h2" sx={contentSectionMenubarTitleSx}>
                Articoli
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Select
                size="small"
                displayEmpty
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
                sx={{ minWidth: 180, ...contentFieldSx }}
              >
                <MenuItem value="">Tutte le categorie</MenuItem>
                {orderedCategories.map((category) => (
                  <MenuItem key={category.name} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openNewArticleTab}
                sx={{ bgcolor: "#0b3d87", whiteSpace: "nowrap", height: 38 }}
              >
                Nuovo articolo
              </Button>
            </Toolbar>
          </AppBar>
          <Box sx={contentPanelFillInsetSx}>
            <AdminTableScrollLayout
            containerSx={contentFillTableContainerSx(loading)}
            tableMinWidth={720}
            head={
              <TableHead sx={adminDataTableHeadSx}>
                <TableRow>
                  <TableCell sx={{ py: 1 }}>Titolo</TableCell>
                  <TableCell sx={{ py: 1 }}>Categoria</TableCell>
                  <TableCell sx={{ py: 1 }}>Pubblicazione</TableCell>
                  <TableCell sx={{ py: 1 }}>Visibile</TableCell>
                  <TableCell align="right" sx={{ py: 1, width: 128 }}>
                    Azioni
                  </TableCell>
                </TableRow>
              </TableHead>
            }
            body={
              <TableBody>
                {!loading && articles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nessun articolo trovato.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  articles.map((article, index) => (
                    <TableRow key={article.id} sx={{ backgroundColor: index % 2 === 0 ? "white" : "#f8fafc" }}>
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {article.title}
                        </Typography>
                        {article.subtitle ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {article.subtitle}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>{article.category || "-"}</TableCell>
                      <TableCell sx={{ py: 0.75 }}>{formatDate(article.publishedAt)}</TableCell>
                      <TableCell sx={{ py: 0.75 }}>{article.isVisible ? "Sì" : "No"}</TableCell>
                      <TableCell align="right" sx={{ py: 0.25 }}>
                        {isActionVisible("gestioneMagazine", "nascondiArticolo") && (
                          <ActionIconButton
                            title={article.isVisible ? "Nascondi articolo" : "Rendi articolo visibile"}
                            onClick={() => handleToggleArticleVisibility(article)}
                            icon={article.isVisible ? VisibilityOffIcon : VisibilityIcon}
                            color={article.isVisible ? "inherit" : "primary"}
                            disabled={visibilityUpdatingId === article.id}
                          />
                        )}
                        {isActionVisible("gestioneMagazine", "modificaArticolo") && (
                          <ActionIconButton
                            title="Modifica articolo"
                            onClick={() => openEditArticleTab(article.id)}
                            icon={EditIcon}
                          />
                        )}
                        {isActionVisible("gestioneMagazine", "eliminaArticolo") && (
                          <ActionIconButton
                            title="Elimina articolo"
                            onClick={() => setDeleteArticleTarget(article)}
                            icon={DeleteIcon}
                            color="#f44336"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            }
          />
          </Box>
        </Paper>
      </Box>
    </Box>
  );

  const renderEditor = () => (
    <Paper elevation={0} sx={contentSectionPaperSx}>
      <ArticleEditorForm
        key={`${editorSessionKey}-${editingArticleId || "new"}`}
        articleId={editingArticleId}
        onSaved={handleArticleSaved}
        onReset={() => {
          clearArticleEditorSession();
          const next = new URLSearchParams(searchParams);
          next.delete("articleId");
          setSearchParams(next, { replace: true });
          setEditorSessionKey((value) => value + 1);
        }}
        onOpenArticle={(id) => openEditArticleTab(id)}
      />
    </Paper>
  );

  return (
    <Box sx={{ ...contentPageShellSx, position: "relative" }}>
      <TableLoadingOverlay loading={loading && tabIndex === 0} />
      <AdminPageHeader
        title="Magazine"
        subtitle="Gestisci articoli e categorie del magazine."
      />
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant={isMobile ? "fullWidth" : "standard"}
        centered={!isMobile}
        sx={contentTabsSx}
      >
        <Tab label="Gestione Magazine" />
        <Tab label="Editor Articoli" />
      </Tabs>

      <Box sx={{ display: tabIndex === 0 ? "block" : "none" }}>{renderGestione()}</Box>
      <Box sx={{ display: tabIndex === 1 ? "block" : "none" }}>{renderEditor()}</Box>

      <ConfirmationModal
        open={Boolean(deleteCategoryTarget)}
        onClose={() => setDeleteCategoryTarget(null)}
        onConfirm={handleDeleteCategory}
        title="Eliminare la categoria?"
        subtitle=""
        message="Se la categoria contiene articoli, l'API bloccherà l'eliminazione e mostrerà il motivo."
        confirmText="ELIMINA"
      />
      <ConfirmationModal
        open={Boolean(deleteArticleTarget)}
        onClose={() => setDeleteArticleTarget(null)}
        onConfirm={handleDeleteArticle}
        title="Eliminare l'articolo?"
        subtitle=""
        message="L'articolo non sarà più disponibile nel magazine."
        confirmText="ELIMINA"
      />
    </Box>
  );
};

export default GestioneMagazineContenuti;
